import React, { useState, useEffect, useCallback } from 'react';
import { get, post, put, del } from '../../hooks/apiFetch';
import MaintenanceModeSection from './MaintenanceModeSection';
import ReleaseNotesManager from './ReleaseNotesManager';
import ManualUrlSection from './ManualUrlSection';
import DatabaseSyncSection from './DatabaseSyncSection';
import { isValidVersion, isDuplicateVersion } from './utils';
import { useAuth } from '../../Auth/AuthContext';

export const MaintenanceTab = () => {
    const [showRepairDBSection, setShowRepairDBSection] = useState(false);
    const [repairDbMessage, setRepairDbMessage] = useState('');
    const [repairDbTypeMessage, setRepairDbTypeMessage] = useState('');
    const [maintenanceModeMessage, setMaintenanceModeMessage] = useState('');
    const [maintenanceModeTypeMessage, setMaintenanceModeTypeMessage] = useState('');
    const [isRepairing, setIsRepairing] = useState(false);
    const [maintenanceStatus, setMaintenanceStatus] = useState({
        isMaintenanceMode: false,
        message: ''
    });
    const [isLoadingMaintenance, setIsLoadingMaintenance] = useState(false);

    // Release Notes state
    const [releaseNotesList, setReleaseNotesList] = useState([]);
    const [isLoadingReleaseNotes, setIsLoadingReleaseNotes] = useState(false);
    const [version, setVersion] = useState('');
    const [content, setContent] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [deletingItems, setDeletingItems] = useState(new Set());
    const [releaseNotesMessage, setReleaseNotesMessage] = useState('');
    const [releaseNotesMessageType, setReleaseNotesMessageType] = useState('');

    // Manual URL state
    const [manualUrl, setManualUrl] = useState('');
    const [isLoadingManualUrl, setIsLoadingManualUrl] = useState(false);
    const [isSavingManualUrl, setIsSavingManualUrl] = useState(false);
    const [manualUrlMessage, setManualUrlMessage] = useState('');
    const [manualUrlMessageType, setManualUrlMessageType] = useState('');

    // Проверка прав доступа
    const { user, permissions, isLoading: authLoading } = useAuth();
    const shouldShowMaintenanceTab = useCallback(() => {
        if (authLoading) return false; // пока загружается – не показываем
        if (!user) return false;

        const allowedEmails = ['a.volzhonok@dt1520.ru', 'a.lyganov@dt1520.ru'];
        return allowedEmails.includes(user.email);
    }, [user, permissions, authLoading]);

    // --- API вызовы ---
    const fetchMaintenanceStatus = useCallback(async () => {
        try {
            const status = await get('/api/app/MaintenanceStatus');
            setMaintenanceStatus(status);
        } catch (error) {
            setMaintenanceModeMessage(`Ошибка при загрузке статуса техработ: ${error.message}`);
            console.error('Ошибка при загрузке статуса техработ:', error);
        }
    }, []);

    const setMaintenanceMode = async (isEnabled) => {
        setIsLoadingMaintenance(true);
        try {
            await post('/api/app/MaintenanceStatus', isEnabled);
            await fetchMaintenanceStatus();
            setMaintenanceModeMessage(
                isEnabled
                    ? 'Режим технических работ успешно включен'
                    : 'Режим технических работ успешно выключен'
            );
            setMaintenanceModeTypeMessage('success');
        } catch (error) {
            console.error('Ошибка:', error);
            setMaintenanceModeMessage(`Ошибка при изменении режима технических работ: ${error.message}`);
            setMaintenanceModeTypeMessage('error');
        } finally {
            setIsLoadingMaintenance(false);
            setTimeout(() => {
                setMaintenanceModeMessage('');
                setMaintenanceModeTypeMessage('');
            }, 5000);
        }
    };

    const fetchReleaseNotes = async () => {
        setIsLoadingReleaseNotes(true);
        try {
            const releaseNotes = await get('/api/app/ReleaseNotes');
            setReleaseNotesList(releaseNotes);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoadingReleaseNotes(false);
        }
    };

    const fetchManualUrl = async () => {
        setIsLoadingManualUrl(true);
        try {
            const url = await get('/api/app/ManualUrl');
            setManualUrl(url || '');
        } catch (error) {
            console.error('Ошибка при загрузке URL руководства:', error);
        } finally {
            setIsLoadingManualUrl(false);
        }
    };

    const handleSaveManualUrl = async ({ url, error, message }) => {
        if (error) {
            setManualUrlMessage(message);
            setManualUrlMessageType('error');
            setTimeout(() => {
                setManualUrlMessage('');
                setManualUrlMessageType('');
            }, 5000);
            return;
        }

        setIsSavingManualUrl(true);
        try {
            await post('/api/app/ManualUrl', url);
            setManualUrlMessage('URL руководства пользователя успешно сохранён');
            setManualUrlMessageType('success');
            await fetchManualUrl();
        } catch (error) {
            console.error('Ошибка сохранения URL руководства пользователя:', error);
            setManualUrlMessage(error.message || 'Произошла ошибка при сохранении URL руководства пользователя');
            setManualUrlMessageType('error');
        } finally {
            setIsSavingManualUrl(false);
            setTimeout(() => {
                setManualUrlMessage('');
                setManualUrlMessageType('');
            }, 5000);
        }
    };

    const handleSaveReleaseNote = async (ver, cont, id) => {
        if (!ver.trim()) {
            setReleaseNotesMessage('Пожалуйста, укажите версию');
            setReleaseNotesMessageType('error');
            setTimeout(() => clearReleaseNotesMessage(), 5000);
            return;
        }
        if (!isValidVersion(ver)) {
            setReleaseNotesMessage('Неверный формат версии. Ожидается: число.число.число[.число] (например 1.2.3 или 1.2.3.4)');
            setReleaseNotesMessageType('error');
            setTimeout(() => clearReleaseNotesMessage(), 5000);
            return;
        }
        if (!cont.trim()) {
            setReleaseNotesMessage('Пожалуйста, введите описание изменений');
            setReleaseNotesMessageType('error');
            setTimeout(() => clearReleaseNotesMessage(), 5000);
            return;
        }

        if (isDuplicateVersion(releaseNotesList, ver, id)) {
            setReleaseNotesMessage(`Версия "${ver}" уже существует. Пожалуйста, используйте уникальную версию.`);
            setReleaseNotesMessageType('error');
            setTimeout(() => clearReleaseNotesMessage(), 5000);
            return;
        }

        setIsSaving(true);
        try {
            if (id) {
                await put(`/api/app/releasenotes/${encodeURIComponent(id)}`, { version: ver, content: cont });
            } else {
                await post('/api/app/releasenotes', { version: ver, content: cont });
            }

            setReleaseNotesMessage(
                id
                    ? `Release Notes версии "${ver}" успешно обновлены`
                    : `Release Notes версии "${ver}" успешно добавлены`
            );
            setReleaseNotesMessageType('success');
            resetReleaseNotesForm();
            await fetchReleaseNotes();
        } catch (error) {
            console.error(error.message);
            setReleaseNotesMessage('Произошла ошибка при сохранении');
            setReleaseNotesMessageType('error');
        } finally {
            setIsSaving(false);
            setTimeout(() => clearReleaseNotesMessage(), 5000);
        }
    };

    const handleEditReleaseNote = (item) => {
        setVersion(item.version);
        setContent(item.content);
        setEditingId(item.id);
        document.getElementById('releaseNotesForm')?.scrollIntoView({ behavior: 'smooth' });
    };

    const resetReleaseNotesForm = () => {
        setVersion('');
        setContent('');
        setEditingId(null);
    };

    const clearReleaseNotesMessage = () => {
        setReleaseNotesMessage('');
        setReleaseNotesMessageType('');
    };

    const handleDeleteReleaseNote = async (id, version) => {
        if (!window.confirm(`Удалить Release Notes версии "${version}"?`)) return;
        setDeletingItems(prev => new Set(prev).add(id));
        try {
            await del(`/api/app/releasenotes/${encodeURIComponent(id)}`);
            setReleaseNotesMessage(`Release Notes версии "${version}" удалены`);
            setReleaseNotesMessageType('success');
            await fetchReleaseNotes();
            if (editingId === id) resetReleaseNotesForm();
        } catch (error) {
            console.error('Ошибка удаления:', error);
            setReleaseNotesMessage(error.message);
            setReleaseNotesMessageType('error');
        } finally {
            setDeletingItems(prev => new Set([...prev].filter(i => i !== id)));
            setTimeout(() => clearReleaseNotesMessage(), 5000);
        }
    };

    const handleRepairDb = useCallback(async () => {
        if (!showRepairDBSection) return;
        setIsRepairing(true);
        try {
            await get('/api/RepairDb/RepairDb');
            setRepairDbMessage('База данных успешно восстановлена');
            setRepairDbTypeMessage('success');
        } catch (error) {
            console.error('Ошибка:', error);
            setRepairDbMessage('Ошибка при восстановлении базы данных');
            setRepairDbTypeMessage('error');
        } finally {
            setIsRepairing(false);
            setTimeout(() => {
                setRepairDbMessage('');
                setRepairDbTypeMessage('');
            }, 5000);
        }
    }, [showRepairDBSection]);

    const handleVersionChange = (e) => {
        let value = e.target.value;
        value = value.replace(/[^\d.]/g, '');
        if (value.startsWith('.')) value = value.slice(1);
        value = value.replace(/\.{2,}/g, '.');
        const parts = value.split('.');
        if (parts.length > 4) {
            value = parts.slice(0, 4).join('.');
        }
        setVersion(value);
    };

    const fetchAll = () => {
        const showTab = shouldShowMaintenanceTab();
        setShowRepairDBSection(showTab);
        Promise.all([fetchMaintenanceStatus(), fetchReleaseNotes(), fetchManualUrl()]);
    };

    useEffect(() => {
        if (!authLoading) {
            fetchAll();
        }
    }, [authLoading]);

    return (
        <div className="maintenance-tab mt-3">
            <div className="maintenance-actions mt-3 ml-3">
                <MaintenanceModeSection
                    maintenanceStatus={maintenanceStatus}
                    onEnable={() => setMaintenanceMode(true)}
                    onDisable={() => setMaintenanceMode(false)}
                    isLoading={isLoadingMaintenance}
                    maintenanceModeMessage={maintenanceModeMessage}
                    maintenanceModeTypeMessage={maintenanceModeTypeMessage}
                />

                <ReleaseNotesManager
                    releaseNotesList={releaseNotesList}
                    isLoading={isLoadingReleaseNotes}
                    onSave={handleSaveReleaseNote}
                    onDelete={handleDeleteReleaseNote}
                    onEdit={handleEditReleaseNote}
                    editingId={editingId}
                    version={version}
                    content={content}
                    onVersionChange={handleVersionChange}
                    onContentChange={setContent}
                    onResetForm={resetReleaseNotesForm}
                    isSaving={isSaving}
                    deletingItems={deletingItems}
                    message={releaseNotesMessage}
                    messageType={releaseNotesMessageType}
                    onClearMessage={clearReleaseNotesMessage}
                />
                {showRepairDBSection &&
                    <ManualUrlSection
                        manualUrl={manualUrl}
                        onUrlChange={setManualUrl}
                        onSave={handleSaveManualUrl}
                        isLoading={isLoadingManualUrl}
                        isSaving={isSavingManualUrl}
                        message={manualUrlMessage}
                        messageType={manualUrlMessageType}
                        notReadyToShow={showRepairDBSection}
                    />
                }

                {showRepairDBSection &&
                    <DatabaseSyncSection
                        onSync={handleRepairDb}
                        isSyncing={isRepairing}
                        repairDbMessage={repairDbMessage}
                        repairDbTypeMessage={repairDbTypeMessage}
                    />
                }
            </div>
        </div>
    );
};